import { ClassDefinitionItemDto } from '@zorro/clients';

import {
  AllowanceModelClassPartType,
  isCustomClassCategoryOnlyDefinition,
} from './class-definitions-extractor';
import {
  ClassMatchingData,
  findMatchingClass,
  isEmployeeMissingRequiredData,
} from './class-matcher';

export interface EmployeeValidationResult {
  isValid: boolean;
  isMissingData: boolean;
  hasClassMismatch: boolean;
  missingFields: string[];
  matchingClassName?: string | null;
}

/**
 * Identifies which specific fields are missing from employee data
 */
export function identifyMissingFields(
  classDefinition: ClassDefinitionItemDto,
  employeeData: ClassMatchingData
): string[] {
  const missingFields: string[] = [];

  if (classDefinition.state && !employeeData.state) {
    missingFields.push(AllowanceModelClassPartType.State);
  }
  if (classDefinition.ratingArea && !employeeData.ratingArea) {
    missingFields.push(AllowanceModelClassPartType.RatingArea);
  }
  if (classDefinition.employmentType && !employeeData.employmentType) {
    missingFields.push(AllowanceModelClassPartType.EmploymentType);
  }
  if (classDefinition.wageType && !employeeData.wageType) {
    missingFields.push(AllowanceModelClassPartType.WageType);
  }
  if (
    classDefinition.customClassCategory &&
    !employeeData.customClassCategory
  ) {
    missingFields.push(AllowanceModelClassPartType.Custom);
  }

  return missingFields;
}

function getClassDefinitionForValidation(
  classDefinitions: ClassDefinitionItemDto[]
): ClassDefinitionItemDto {
  for (const def of classDefinitions) {
    if (isCustomClassCategoryOnlyDefinition(def)) {
      return def;
    }
  }
  return classDefinitions[0];
}

/**
 * Validates employee data against class definitions
 * Returns comprehensive validation result including missing fields and class mismatch info
 */
export function validateEmployeeDataAgainstClassDefinitions(
  classDefinitions: ClassDefinitionItemDto[],
  employeeData: ClassMatchingData
): EmployeeValidationResult {
  if (!classDefinitions?.length) {
    return {
      isValid: true,
      isMissingData: false,
      hasClassMismatch: false,
      missingFields: [],
    };
  }

  const sampleClassDefinition =
    getClassDefinitionForValidation(classDefinitions);

  const isMissingData = isEmployeeMissingRequiredData(
    sampleClassDefinition,
    employeeData
  );

  let hasClassMismatch = false;
  let missingFields: string[] = [];

  let matchingClassName: string | null = null;

  if (isMissingData) {
    missingFields = identifyMissingFields(sampleClassDefinition, employeeData);
  } else {
    // Only check for class mismatch if we have all required data
    matchingClassName = findMatchingClass(classDefinitions, employeeData);
    hasClassMismatch = !matchingClassName;
  }

  const isValid = !isMissingData && !hasClassMismatch;

  return {
    isValid,
    isMissingData,
    hasClassMismatch,
    missingFields,
    matchingClassName,
  };
}

/**
 * Extracts employee data in the format needed for validation
 */
export function extractEmployeeDataForValidation(employee: {
  state?: string | null;
  ratingArea?: string | null;
  employmentType?: string | null;
  wageType?: string | null;
  customClassCategory?: string | null;
}): ClassMatchingData {
  return {
    state: employee.state,
    ratingArea: employee.ratingArea,
    employmentType: employee.employmentType,
    wageType: employee.wageType,
    customClassCategory: employee.customClassCategory,
  };
}
